import {
  functionalUpdate,
  type ColumnOrderState,
  type ColumnPinningState,
  type OnChangeFn,
  type PaginationState,
  type RowSelectionState,
  type SortingState,
  type VisibilityState
} from "@tanstack/react-table";
import { useCallback, useDeferredValue, useEffect, useMemo, useRef, useState } from "react";
import { AccountApiError } from "../lib/account-api";
import { createTickerFilingAlert } from "../lib/alerts-api";
import { DashboardDataError, loadDashboardData } from "../lib/dashboard-data";
import {
  applyCanonicalScreener,
  buildCanonicalSummary,
  type CanonicalScreenerCriteria
} from "../lib/filters";
import { downloadTransactionsCsv } from "../lib/export-results";
import {
  BUILT_IN_VIEWS,
  DEFAULT_COLUMN_VISIBILITY,
  DEFAULT_SCREENER_STATE,
  SCREENER_COLUMN_IDS,
  createDefaultScreenerState,
  isScreenerPageSize,
  isScreenerSortField,
  limitScreenerSorts,
  parseScreenerState,
  serializeScreenerState
} from "../lib/screener-state";
import { buildEntityDossier, parseEntitySearchParam, type EntityRef } from "../lib/entity-dossier";
import type {
  BuiltInViewId,
  CanonicalTransaction,
  DashboardLoadState,
  ScreenerFilterState,
  ScreenerState
} from "../lib/types";
import { CoverageSheet } from "./CoverageSheet";
import { EntityDossier } from "./EntityDossier";
import { WorkspaceDrawer } from "./WorkspaceDrawer";
import { DashboardHeader } from "./DashboardHeader";
import { DashboardShell } from "./DashboardShell";
import { EmptyState } from "./EmptyState";
import { ErrorBanner } from "./ErrorBanner";
import { FilterPanel } from "./FilterPanel";
import { InsiderTable, type InsiderTableFocusRequest } from "./InsiderTable";
import { MobileFilterSheet } from "./MobileFilterSheet";
import { NavigationRail } from "./NavigationRail";
import { ScreenerSummary } from "./ScreenerSummary";
import { SkeletonTable } from "./SkeletonTable";
import { TableControls } from "./TableControls";
import { TableToolbar } from "./TableToolbar";
import {
  TransactionInspector,
  TransactionInspectorSheet,
  type TransactionInspectorProps
} from "./TransactionInspector";
import { TrustStatusBar } from "./TrustStatusBar";
import { useAccountPersistence } from "./useAccountPersistence";
import { SignInDialog } from "./SignInDialog";
import { AuthGate } from "./AuthGate";
import { ActivityFeedStrip } from "./ActivityFeedStrip";
import {
  buildActivityFeedStats,
  readLastVisitFilingDate,
  writeLastVisitFilingDate
} from "../lib/activity-feed";
import { signOut } from "../lib/auth-api";
import { useMediaQuery } from "./useMediaQuery";

const EMPTY_TRANSACTIONS: CanonicalTransaction[] = [];
const NARROW_DEFAULT_COLUMNS = new Set([
  "favorite",
  "ticker",
  "signal",
  "filingDate",
  "shares",
  "price",
  "value",
  "score",
  "details"
]);
const NARROW_DEFAULT_COLUMN_PINNING: ColumnPinningState = {
  left: ["favorite", "ticker"],
  right: ["details"]
};
const RECENT_SCREENING_RESULT_LIMIT = 4;

interface CachedScreeningResult {
  filteredTransactions: CanonicalTransaction[];
  summary: ReturnType<typeof buildCanonicalSummary>;
}

function createBoundedScreeningSelector(
  transactions: readonly CanonicalTransaction[],
  favoriteTickers: readonly string[]
) {
  const cache = new Map<string, CachedScreeningResult>();
  return (criteria: CanonicalScreenerCriteria): CachedScreeningResult => {
    const cacheKey = JSON.stringify(criteria);
    const cached = cache.get(cacheKey);
    if (cached) return cached;

    const filteredTransactions = applyCanonicalScreener(
      transactions,
      criteria,
      favoriteTickers
    );
    const result = {
      filteredTransactions,
      summary: buildCanonicalSummary(filteredTransactions)
    };
    if (cache.size >= RECENT_SCREENING_RESULT_LIMIT) {
      const oldestKey = cache.keys().next().value;
      if (oldestKey !== undefined) cache.delete(oldestKey);
    }
    cache.set(cacheKey, result);
    return result;
  };
}

function effectiveColumnVisibility(
  visibility: Readonly<Record<string, boolean>>,
  wideTable: boolean
): VisibilityState {
  const hasExplicitVisibility = Object.keys(visibility).length > 0;
  if (!hasExplicitVisibility) {
    // Empty map is treated as product default, not "show every column".
    // Narrow screens still collapse further for density.
    return Object.fromEntries(
      SCREENER_COLUMN_IDS.map((columnId) => [
        columnId,
        wideTable
          ? DEFAULT_COLUMN_VISIBILITY[columnId]
          : NARROW_DEFAULT_COLUMNS.has(columnId)
      ])
    );
  }
  return Object.fromEntries(
    SCREENER_COLUMN_IDS.map((columnId) => [
      columnId,
      visibility[columnId] ?? DEFAULT_COLUMN_VISIBILITY[columnId] ?? true
    ])
  );
}

function sameColumnIds(left: readonly string[] | undefined, right: readonly string[]): boolean {
  return (
    left?.length === right.length && left.every((columnId, index) => columnId === right[index])
  );
}

function effectiveColumnPinning(
  pinning: ScreenerState["columnPinning"],
  wideTable: boolean
): ColumnPinningState {
  const usesDefaultPinning =
    sameColumnIds(pinning.left, DEFAULT_SCREENER_STATE.columnPinning.left) &&
    sameColumnIds(pinning.right, DEFAULT_SCREENER_STATE.columnPinning.right);
  return wideTable || !usesDefaultPinning ? pinning : NARROW_DEFAULT_COLUMN_PINNING;
}

interface InspectorActivation {
  target: HTMLElement | null;
  transactionId: string;
}

function initialScreenerState(): ScreenerState {
  return typeof window === "undefined"
    ? createDefaultScreenerState()
    : parseScreenerState(window.location.search);
}

function normalizedError(error: unknown): Error {
  return error instanceof Error ? error : new Error(String(error));
}

function serializedDashboardUrl(state: ScreenerState): string {
  if (typeof window === "undefined") return "/dashboard";
  const url = new URL(window.location.href);
  url.search = serializeScreenerState(state);
  return url.href;
}

export function DashboardApp() {
  const [loadState, setLoadState] = useState<DashboardLoadState>({ availability: "loading" });
  const [loadAttempt, setLoadAttempt] = useState(0);
  const [coverageOpen, setCoverageOpen] = useState(false);
  const [workspaceOpen, setWorkspaceOpen] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState<"watchlists" | "alerts" | "history" | "notes">("alerts");
  const [alertPendingTicker, setAlertPendingTicker] = useState<string | null>(null);
  const [alertNotice, setAlertNotice] = useState<string | null>(null);
  const [entityRef, setEntityRef] = useState<EntityRef | null>(() =>
    typeof window === "undefined"
      ? null
      : parseEntitySearchParam(new URLSearchParams(window.location.search).get("entity"))
  );
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [signInOpen, setSignInOpen] = useState(false);
  const [screenerState, setScreenerState] = useState<ScreenerState>(initialScreenerState);
  const {
    session,
    favoriteTickers,
    savedViews,
    favoriteFailure,
    savedViewFailure,
    accountReadState,
    favoriteMutationPending,
    savedViewMutationPending,
    canUseFavorites,
    selectUserSavedView,
    createUserSavedView,
    renameUserSavedView,
    deleteUserSavedView,
    toggleFavoriteTicker,
    retryAccountData,
    retryFavoriteMutation,
    retrySavedViewMutation
  } = useAccountPersistence({ screenerState, setScreenerState });
  const toggleFavoriteTickerRef = useRef(toggleFavoriteTicker);
  toggleFavoriteTickerRef.current = toggleFavoriteTicker;
  const handleToggleFavorite = useCallback((ticker: string) => {
    void toggleFavoriteTickerRef.current(ticker);
  }, []);
  const tableScrollerRef = useRef<HTMLDivElement>(null);
  const inspectorActivationRef = useRef<InspectorActivation | null>(null);
  const [tableFocusRequest, setTableFocusRequest] =
    useState<InsiderTableFocusRequest | null>(null);
  const tableFocusRequestRef = useRef<InsiderTableFocusRequest | null>(null);
  const tableFocusRequestSequenceRef = useRef(0);
  const wideTable = useMediaQuery("(min-width: 1024px)");
  const inlineInspector = useMediaQuery("(min-width: 1600px)");
  const previousInlineInspectorRef = useRef(inlineInspector);
  const nextDashboardUrl = serializedDashboardUrl(screenerState);
  // Gate only on identity resolution. Favorites/views may still be loading after sign-in.
  const isAuthenticated = session?.authenticated === true;
  const sessionBlocked =
    accountReadState.availability === "error" &&
    accountReadState.kind === "session" &&
    !isAuthenticated;
  const sessionResolved =
    session !== null || sessionBlocked || accountReadState.availability === "ready";
  const requiresAuthGate = !sessionResolved || sessionBlocked || !isAuthenticated;

  useEffect(() => {
    if (typeof performance?.mark === "function") performance.mark("trackinsiders-shell-ready");
  }, []);


  useEffect(() => {
    if (!isAuthenticated) {
      setLoadState({ availability: "loading" });
      return;
    }
    let isCurrent = true;
    setLoadState({ availability: "loading" });
    void loadDashboardData().then(
      (loaded) => {
        if (!isCurrent) return;
        setLoadState({ availability: "ready", ...loaded });
      },
      (error: unknown) => {
        if (!isCurrent) return;
        setLoadState({ availability: "error", error: normalizedError(error) });
      }
    );
    return () => {
      isCurrent = false;
    };
  }, [loadAttempt, isAuthenticated]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;
    const handlePopState = () => setScreenerState(parseScreenerState(window.location.search));
    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (nextDashboardUrl !== window.location.href) {
      window.history.replaceState(window.history.state, "", nextDashboardUrl);
    }
  }, [nextDashboardUrl]);

  const readyData = loadState.availability === "ready" ? loadState : null;

  useEffect(() => {
    if (readyData && typeof performance?.mark === "function") {
      performance.mark("trackinsiders-data-ready");
    }
  }, [readyData]);
  const transactions = readyData?.transactions ?? EMPTY_TRANSACTIONS;
  const screeningCriteria = useMemo<CanonicalScreenerCriteria>(
    () => ({
      builtInViewId: screenerState.builtInViewId,
      filters: screenerState.filters,
      sorting: screenerState.sorting
    }),
    [screenerState.builtInViewId, screenerState.filters, screenerState.sorting]
  );
  const deferredScreeningCriteria = useDeferredValue(screeningCriteria);
  const screeningCriteriaPending = deferredScreeningCriteria !== screeningCriteria;
  const favoritesCriteriaPending =
    (deferredScreeningCriteria.builtInViewId === "my-watchlist" ||
      deferredScreeningCriteria.filters.favoritesOnly) &&
    accountReadState.availability !== "ready";
  const selectScreeningResult = useMemo(
    () => createBoundedScreeningSelector(transactions, favoriteTickers),
    [favoriteTickers, transactions]
  );
  const { filteredTransactions, summary } = useMemo(
    () => selectScreeningResult(deferredScreeningCriteria),
    [deferredScreeningCriteria, selectScreeningResult]
  );
  const pageCount = Math.max(1, Math.ceil(filteredTransactions.length / screenerState.pageSize));
  const safePage = Math.min(screenerState.page, pageCount - 1);
  const pageStart = safePage * screenerState.pageSize;
  const currentPageTransactions = useMemo(
    () => filteredTransactions.slice(pageStart, pageStart + screenerState.pageSize),
    [filteredTransactions, pageStart, screenerState.pageSize]
  );
  const tablePagination = useMemo<PaginationState>(
    () => ({ pageIndex: safePage, pageSize: screenerState.pageSize }),
    [safePage, screenerState.pageSize]
  );
  const visibleColumns = useMemo(
    () => effectiveColumnVisibility(screenerState.columnVisibility, wideTable),
    [screenerState.columnVisibility, wideTable]
  );
  const tableColumnPinning = useMemo(
    () => effectiveColumnPinning(screenerState.columnPinning, wideTable),
    [screenerState.columnPinning, wideTable]
  );
  const exchangeOptions = useMemo(
    () => readyData?.manifest.coverage.exchangeCoverage.map((item) => item.exchange) ?? [],
    [readyData]
  );
  const selectedTransaction =
    readyData && screenerState.selectedTransactionId
      ? (readyData.indexes.byId.get(screenerState.selectedTransactionId) ?? null)
      : null;
  const filteredIndexById = useMemo(() => {
    const indexById = new Map<string, number>();
    filteredTransactions.forEach((transaction, index) => {
      indexById.set(transaction.transactionId, index);
    });
    return indexById;
  }, [filteredTransactions]);
  const selectedResultIndex = selectedTransaction
    ? (filteredIndexById.get(selectedTransaction.transactionId) ?? -1)
    : -1;
  useEffect(() => {
    if (
      !readyData ||
      screeningCriteriaPending ||
      favoritesCriteriaPending ||
      screenerState.page === safePage
    ) {
      return;
    }
    setScreenerState((current) => {
      if (
        current.builtInViewId !== deferredScreeningCriteria.builtInViewId ||
        current.filters !== deferredScreeningCriteria.filters ||
        current.sorting !== deferredScreeningCriteria.sorting
      ) {
        return current;
      }
      return current.page === safePage ? current : { ...current, page: safePage };
    });
  }, [
    deferredScreeningCriteria,
    favoritesCriteriaPending,
    readyData,
    safePage,
    screenerState.page,
    screeningCriteriaPending
  ]);

  useEffect(() => {
    if (!readyData || screenerState.selectedTransactionId === null) return;
    if (readyData.indexes.byId.has(screenerState.selectedTransactionId)) return;
    setScreenerState((current) =>
      current.selectedTransactionId === null
        ? current
        : { ...current, selectedTransactionId: null }
    );
  }, [readyData, screenerState.selectedTransactionId]);

  const updateFilters = useCallback((patch: Partial<ScreenerFilterState>) => {
    setScreenerState((current) => ({
      ...current,
      filters: { ...current.filters, ...patch },
      page: 0,
      savedViewId: null
    }));
  }, []);

  const selectBuiltInView = useCallback((id: BuiltInViewId) => {
    const definition = BUILT_IN_VIEWS[id];
    setScreenerState((current) => ({
      ...current,
      builtInViewId: id,
      sorting: definition.defaultSort.map((sort) => ({ ...sort })),
      savedViewId: null,
      page: 0
    }));
  }, []);

  const resetFilters = useCallback(() => {
    const defaults = createDefaultScreenerState();
    setScreenerState((current) => ({
      ...current,
      filters: defaults.filters,
      sorting: defaults.sorting,
      builtInViewId: defaults.builtInViewId,
      savedViewId: null,
      page: 0
    }));
  }, []);

  const updateSorting = useCallback<OnChangeFn<SortingState>>((updater) => {
    setScreenerState((current) => {
      const nextSorting = functionalUpdate(updater, current.sorting).flatMap((sort) =>
        isScreenerSortField(sort.id) ? [{ id: sort.id, desc: sort.desc }] : []
      );
      return {
        ...current,
        sorting: limitScreenerSorts(nextSorting),
        page: 0,
        savedViewId: null
      };
    });
  }, []);

  const updatePagination = useCallback<OnChangeFn<PaginationState>>((updater) => {
    setScreenerState((current) => {
      const pagination = functionalUpdate(updater, {
        pageIndex: current.page,
        pageSize: current.pageSize
      });
      return {
        ...current,
        page: pagination.pageIndex,
        pageSize: isScreenerPageSize(pagination.pageSize)
          ? pagination.pageSize
          : current.pageSize
      };
    });
  }, []);

  const updateColumnVisibility = useCallback<OnChangeFn<VisibilityState>>((updater) => {
    setScreenerState((current) => ({
      ...current,
      columnVisibility: functionalUpdate(
        updater,
        effectiveColumnVisibility(current.columnVisibility, wideTable)
      ),
      savedViewId: null
    }));
  }, [wideTable]);

  const updateColumnOrder = useCallback<OnChangeFn<ColumnOrderState>>((updater) => {
    setScreenerState((current) => ({
      ...current,
      columnOrder: [...functionalUpdate(updater, current.columnOrder)],
      savedViewId: null
    }));
  }, []);

  const updateColumnPinning = useCallback<OnChangeFn<ColumnPinningState>>((updater) => {
    setScreenerState((current) => {
      const pinning = functionalUpdate(
        updater,
        effectiveColumnPinning(current.columnPinning, wideTable)
      );
      return {
        ...current,
        columnPinning: {
          left: [...(pinning.left ?? [])],
          right: [...(pinning.right ?? [])]
        },
        savedViewId: null
      };
    });
  }, [wideTable]);

  const updateRowSelection = useCallback<OnChangeFn<RowSelectionState>>((updater) => {
    setScreenerState((current) => {
      const currentSelection: RowSelectionState = current.selectedTransactionId
        ? { [current.selectedTransactionId]: true }
        : {};
      const nextSelection = functionalUpdate(updater, currentSelection);
      const selectedTransactionId =
        Object.entries(nextSelection).find(([, selected]) => selected)?.[0] ?? null;
      return current.selectedTransactionId === selectedTransactionId
        ? current
        : { ...current, selectedTransactionId };
    });
  }, []);

  const updateDensity = useCallback((density: ScreenerState["density"]) => {
    setScreenerState((current) => ({
      ...current,
      density,
      savedViewId: null
    }));
  }, []);

  const openAlertsWorkspace = useCallback(() => {
    setWorkspaceTab("alerts");
    setWorkspaceOpen(true);
  }, []);

  const activateTransaction = useCallback((transactionId: string, target: HTMLElement) => {
    inspectorActivationRef.current = { target, transactionId };
  }, []);

  const completeTableFocusRequest = useCallback((requestId: number, focused: boolean) => {
    if (tableFocusRequestRef.current?.requestId !== requestId) return;
    tableFocusRequestRef.current = null;
    setTableFocusRequest((current) =>
      current?.requestId === requestId ? null : current
    );
    if (!focused) tableScrollerRef.current?.focus({ preventScroll: true });
  }, []);

  const selectInspectorResult = (transactionId: string) => {
    const resultIndex = filteredTransactions.findIndex(
      (transaction) => transaction.transactionId === transactionId
    );
    if (resultIndex < 0) return;
    if (inspectorActivationRef.current) {
      inspectorActivationRef.current.transactionId = transactionId;
    }
    setScreenerState((current) => ({
      ...current,
      selectedTransactionId: transactionId,
      page: Math.floor(resultIndex / current.pageSize)
    }));
  };

  const closeInspector = useCallback(() => {
    const transactionId = screenerState.selectedTransactionId;
    const activation = inspectorActivationRef.current;
    setScreenerState((current) =>
      current.selectedTransactionId === null
        ? current
        : { ...current, selectedTransactionId: null }
    );
    if (typeof window === "undefined") return;
    const restoreFocus = () => {
      if (inspectorActivationRef.current !== activation) return;
      const activationRow = activation?.target?.closest<HTMLElement>("[data-transaction-id]");
      if (
        activation?.target?.isConnected === true &&
        activationRow?.dataset.transactionId === transactionId
      ) {
        activation.target.focus({ preventScroll: true });
        inspectorActivationRef.current = null;
        return;
      }
      // Prefer re-querying the details control when the original node remounted
      // (virtualized row recycle or column chrome changes with alert actions).
      if (transactionId !== null && tableScrollerRef.current?.isConnected === true) {
        const liveRow = Array.from(
          tableScrollerRef.current.querySelectorAll<HTMLElement>("tr[data-transaction-id]")
        ).find((row) => row.dataset.transactionId === transactionId);
        const detailsButton = liveRow?.querySelector<HTMLElement>(
          'button[aria-label^="Open details for "]'
        );
        if (detailsButton) {
          detailsButton.focus({ preventScroll: true });
          inspectorActivationRef.current = null;
          return;
        }
        if (liveRow) {
          liveRow.focus({ preventScroll: true });
          inspectorActivationRef.current = null;
          return;
        }
        const request = {
          requestId: tableFocusRequestSequenceRef.current + 1,
          transactionId
        };
        tableFocusRequestSequenceRef.current = request.requestId;
        tableFocusRequestRef.current = request;
        setTableFocusRequest(request);
        inspectorActivationRef.current = null;
        return;
      }
      tableScrollerRef.current?.focus({ preventScroll: true });
      inspectorActivationRef.current = null;
    };
    if (typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(restoreFocus);
    } else {
      window.setTimeout(restoreFocus, 0);
    }
  }, [screenerState.selectedTransactionId]);

  useEffect(() => {
    if (!inlineInspector || screenerState.selectedTransactionId === null) return undefined;
    const handleEscape = (event: globalThis.KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      closeInspector();
    };
    window.addEventListener("keydown", handleEscape);
    return () => window.removeEventListener("keydown", handleEscape);
  }, [closeInspector, inlineInspector, screenerState.selectedTransactionId]);

  useEffect(() => {
    const previousInlineInspector = previousInlineInspectorRef.current;
    previousInlineInspectorRef.current = inlineInspector;
    if (
      previousInlineInspector === inlineInspector ||
      screenerState.selectedTransactionId === null
    ) {
      return;
    }
    document
      .querySelector<HTMLElement>("[data-inspector-focus-root]")
      ?.focus({ preventScroll: true });
  }, [inlineInspector, screenerState.selectedTransactionId]);

  const retryDashboardData = () => {
    setLoadState({ availability: "loading" });
    setLoadAttempt((current) => current + 1);
  };

  useEffect(() => {
    if (typeof window === "undefined") return;
    const url = new URL(window.location.href);
    if (entityRef) url.searchParams.set("entity", `${entityRef.kind}:${entityRef.id}`);
    else url.searchParams.delete("entity");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [entityRef]);

  const activeDossier =
    readyData && entityRef ? buildEntityDossier(entityRef, readyData.indexes) : null;

  const exportResults = () => {
    if (
      !readyData ||
      screeningCriteriaPending ||
      favoritesCriteriaPending ||
      filteredTransactions.length === 0
    ) {
      return;
    }
    downloadTransactionsCsv(
      filteredTransactions,
      `trackinsiders-${readyData.manifest.generatedAt.slice(0, 10)}.csv`
    );
  };

  const handleCreateTickerAlert = async (ticker: string) => {
    if (!session?.authenticated) {
      setSignInOpen(true);
      return;
    }
    const normalized = ticker.trim().toUpperCase();
    if (!normalized || alertPendingTicker) return;
    setAlertPendingTicker(normalized);
    setAlertNotice(null);
    try {
      const rule = await createTickerFilingAlert(normalized);
      setAlertNotice(`Alert rule “${rule.name}” is on. Manage it in Workspace → Alerts.`);
      openAlertsWorkspace();
    } catch (error) {
      setAlertNotice(
        error instanceof Error ? error.message : `Could not create alert for ${normalized}`
      );
      openAlertsWorkspace();
    } finally {
      setAlertPendingTicker(null);
    }
  };

  const filterProps = {
    state: screenerState,
    exchangeOptions,
    onFilterPatch: updateFilters,
    onReset: resetFilters
  };

  const accountError =
    accountReadState.availability === "error" ? accountReadState : null;
  const loadError = loadState.availability === "error" ? loadState.error : null;
  const typedLoadError = loadError instanceof DashboardDataError ? loadError : null;
  const inspectorProps: TransactionInspectorProps | null =
    readyData && selectedTransaction
      ? {
          transaction: selectedTransaction,
          manifest: readyData.manifest,
          indexes: readyData.indexes,
          orderedResults: filteredTransactions,
          resultIndex: selectedResultIndex,
          favoriteTickers,
          canUseFavorites,
          favoriteMutationPending,
          canCreateAlerts: Boolean(session?.authenticated),
          alertMutationPending: alertPendingTicker !== null,
          onToggleFavorite: handleToggleFavorite,
          onCreateTickerAlert: handleCreateTickerAlert,
          onSelectResult: selectInspectorResult,
          onClose: closeInspector,
          onOpenIssuer: (issuerCik: string) => setEntityRef({ kind: "issuer", id: issuerCik }),
          onOpenOwner: (ownerIdentityKey: string) => setEntityRef({ kind: "owner", id: ownerIdentityKey }),
          onOpenCluster: (clusterId: string) => setEntityRef({ kind: "cluster", id: clusterId })
        }
      : null;

  if (requiresAuthGate) {
    return (
      <>
        <AuthGate
          status={!sessionResolved ? "loading" : sessionBlocked ? "error" : "signed-out"}
          errorMessage={
            sessionBlocked && accountReadState.availability === "error"
              ? accountReadState.error.message
              : undefined
          }
          onSignIn={() => setSignInOpen(true)}
          onRetry={sessionBlocked ? retryAccountData : undefined}
        />
        <SignInDialog
          open={signInOpen}
          onOpenChange={setSignInOpen}
          redirectUrl={nextDashboardUrl}
        />
      </>
    );
  }

  return (
    <>
      <DashboardShell
        dashboardState={loadState.availability}
        indexedRowCount={readyData?.transactions.length}
        navigationRail={
          <NavigationRail
            activeViewId={screenerState.builtInViewId}
            coverageOpen={coverageOpen}
            onSelectScreener={() => selectBuiltInView("all-activity")}
            onSelectWatchlist={() => selectBuiltInView("my-watchlist")}
            onOpenCoverage={() => setCoverageOpen(true)}
          />
        }
        header={
          <DashboardHeader
            query={screenerState.filters.query}
            onQueryChange={(query) => updateFilters({ query })}
            session={session}
            favoriteCount={favoriteTickers.length}
            onSignIn={() => setSignInOpen(true)}
            onSignOut={() => {
              void signOut().then(() => window.location.reload());
            }}
            accountAvailability={accountReadState.availability}
            canExport={
              readyData !== null &&
              !screeningCriteriaPending &&
              !favoritesCriteriaPending &&
              filteredTransactions.length > 0
            }
            onExportResults={exportResults}
            onOpenWorkspace={openAlertsWorkspace}
          />
        }
        trustStatus={
          loadState.availability === "loading" ? (
            <TrustStatusBar availability="loading" />
          ) : loadState.availability === "error" ? (
            <TrustStatusBar availability="error" />
          ) : (
            <TrustStatusBar
              availability="ready"
              manifest={loadState.manifest}
              health={loadState.health}
              loadedRowCount={loadState.transactions.length}
              onOpenCoverage={() => setCoverageOpen(true)}
            />
          )
        }
        filterPanel={<FilterPanel {...filterProps} />}
        inspector={
          inlineInspector && inspectorProps ? (
            <TransactionInspector {...inspectorProps} />
          ) : undefined
        }
      >
        <div className="flex min-h-0 flex-col">
          {accountError ? (
            <ErrorBanner
              ariaLabel={
                accountError.kind === "session"
                  ? "Session data unavailable"
                  : accountError.kind === "favorites"
                    ? "Favorites data unavailable"
                    : "Saved views data unavailable"
              }
              title={
                accountError.kind === "session"
                  ? "Account session could not be loaded."
                  : accountError.kind === "favorites"
                    ? "Favorite tickers could not be loaded."
                    : "Saved views could not be loaded."
              }
              diagnosticCode={
                accountError.error instanceof AccountApiError
                  ? accountError.error.code
                  : "account_read_unexpected"
              }
              actionLabel="Retry account data"
              onAction={retryAccountData}
            />
          ) : null}
          {favoriteFailure ? (
            <ErrorBanner
              ariaLabel="Favorite update failed"
              title={favoriteFailure.message}
              actionLabel="Retry favorite update"
              onAction={retryFavoriteMutation}
            />
          ) : null}
          {savedViewFailure ? (
            <ErrorBanner
              ariaLabel="Saved view operation failed"
              title={savedViewFailure.message}
              actionLabel="Retry saved-view operation"
              onAction={retrySavedViewMutation}
            />
          ) : null}

          {loadState.availability === "loading" ? <SkeletonTable /> : null}
          {loadError ? (
            <ErrorBanner
              variant="blocking"
              ariaLabel="Dashboard data unavailable"
              title="Unable to load dashboard data"
              message={loadError.message}
              diagnosticCode={typedLoadError?.code ?? "unexpected_error"}
              httpStatus={typedLoadError?.httpStatus}
              shardUrl={typedLoadError?.shardUrl}
              actionLabel="Retry data load"
              onAction={retryDashboardData}
            />
          ) : null}
          {readyData ? (
            <>
              <ActivityFeedFromData
                transactions={readyData.transactions}
                favoriteTickers={favoriteTickers}
                onOpenToday={() => selectBuiltInView("today")}
                onOpenActionQueue={() => selectBuiltInView("action-queue")}
                onOpenWorkspace={openAlertsWorkspace}
              />
              <ScreenerSummary summary={summary} />
              <TableToolbar
                state={screenerState}
                savedViews={savedViews}
                authenticated={session?.authenticated === true}
                accountAvailability={accountReadState.availability}
                savedViewMutationPending={savedViewMutationPending}
                onSignIn={() => setSignInOpen(true)}
                pageIndex={safePage}
                totalRows={filteredTransactions.length}
                onFilterPatch={updateFilters}
                onSelectBuiltInView={selectBuiltInView}
                onSelectSavedView={selectUserSavedView}
                onCreateSavedView={createUserSavedView}
                onRenameSavedView={renameUserSavedView}
                onDeleteSavedView={deleteUserSavedView}
                onPageSizeChange={(pageSize) =>
                  setScreenerState((current) => ({ ...current, pageSize, page: 0 }))
                }
                onPrevious={() =>
                  setScreenerState((current) => ({
                    ...current,
                    page: Math.max(0, safePage - 1)
                  }))
                }
                onNext={() =>
                  setScreenerState((current) => ({
                    ...current,
                    page: Math.min(pageCount - 1, safePage + 1)
                  }))
                }
                onOpenFilters={() => setMobileFiltersOpen(true)}
                tableControls={
                  <TableControls
                    density={screenerState.density}
                    columnVisibility={visibleColumns}
                    columnOrder={screenerState.columnOrder}
                    columnPinning={tableColumnPinning}
                    onDensityChange={updateDensity}
                    onColumnVisibilityChange={updateColumnVisibility}
                    onColumnOrderChange={updateColumnOrder}
                    onColumnPinningChange={updateColumnPinning}
                  />
                }
              />
              {filteredTransactions.length === 0 ? (
                <EmptyState
                  kind={
                    screenerState.builtInViewId === "my-watchlist" && session?.authenticated === false
                      ? "signed-out-watchlist"
                      : transactions.length === 0
                        ? "dataset"
                      : "filtered"
                  }
                  onReset={resetFilters}
                  onSignIn={() => setSignInOpen(true)}
                  datasetMode={
                    readyData?.manifest.datasetMode === "live"
                      ? undefined
                      : readyData?.manifest.datasetMode
                  }
                />
              ) : (
                <InsiderTable
                  rows={currentPageTransactions}
                  totalResultCount={filteredTransactions.length}
                  favoriteTickers={favoriteTickers}
                  canUseFavorites={canUseFavorites && !favoriteMutationPending}
                  sorting={screenerState.sorting}
                  pagination={tablePagination}
                  columnVisibility={visibleColumns}
                  columnOrder={screenerState.columnOrder}
                  columnPinning={tableColumnPinning}
                  density={screenerState.density}
                  selectedTransactionId={screenerState.selectedTransactionId}
                  focusRequest={tableFocusRequest}
                  scrollerRef={tableScrollerRef}
                  onToggleFavorite={handleToggleFavorite}
                  canCreateAlerts={Boolean(session?.authenticated)}
                  alertPendingTicker={alertPendingTicker}
                  onCreateTickerAlert={handleCreateTickerAlert}
                  onSortingChange={updateSorting}
                  onPaginationChange={updatePagination}
                  onColumnVisibilityChange={updateColumnVisibility}
                  onColumnOrderChange={updateColumnOrder}
                  onColumnPinningChange={updateColumnPinning}
                  onRowSelectionChange={updateRowSelection}
                  onActivate={activateTransaction}
                  onFocusRequestComplete={completeTableFocusRequest}
                />
              )}
            </>
          ) : null}
        </div>
      </DashboardShell>

      <WorkspaceDrawer
        open={workspaceOpen}
        authenticated={Boolean(session?.authenticated)}
        activeTicker={selectedTransaction?.ticker ?? null}
        sessionEmail={session?.email ?? null}
        initialTab={workspaceTab}
        alertNotice={alertNotice}
        onClose={() => {
          setWorkspaceOpen(false);
          setAlertNotice(null);
        }}
        onAlertCreated={(rule) => {
          setAlertNotice(`Alert rule “${rule.name}” saved.`);
        }}
      />
      {activeDossier ? (
        <div className="fixed inset-y-0 right-0 z-40 w-full max-w-md shadow-2xl">
          <EntityDossier
            dossier={activeDossier}
            onSelectTransaction={(transactionId) => {
              setScreenerState((current) => ({ ...current, selectedTransactionId: transactionId }));
            }}
            onClose={() => setEntityRef(null)}
          />
        </div>
      ) : null}
      <CoverageSheet
        open={coverageOpen}
        onOpenChange={setCoverageOpen}
        manifest={readyData?.manifest ?? null}
        health={readyData?.health ?? null}
        loadedRowCount={readyData?.transactions.length ?? 0}
        loadedShardCount={readyData?.manifest.transactionShardUrls.length ?? 0}
      />
      <MobileFilterSheet
        {...filterProps}
        open={mobileFiltersOpen}
        onOpenChange={setMobileFiltersOpen}
      />
      {!inlineInspector && inspectorProps ? (
        <TransactionInspectorSheet {...inspectorProps} />
      ) : null}
      <SignInDialog
        open={signInOpen}
        onOpenChange={setSignInOpen}
        redirectUrl={nextDashboardUrl}
      />
    </>
  );
}

function ActivityFeedFromData({
  transactions,
  favoriteTickers,
  onOpenToday,
  onOpenActionQueue,
  onOpenWorkspace
}: {
  transactions: readonly CanonicalTransaction[];
  favoriteTickers: readonly string[];
  onOpenToday: () => void;
  onOpenActionQueue: () => void;
  onOpenWorkspace: () => void;
}) {
  // Read localStorage once per mount; per-render reads would defeat the memo below.
  const [previousVisit] = useState(() => readLastVisitFilingDate());
  const stats = useMemo(
    () => buildActivityFeedStats(transactions, favoriteTickers, previousVisit),
    [transactions, favoriteTickers, previousVisit]
  );
  useEffect(() => {
    writeLastVisitFilingDate(stats.latestFilingDate);
  }, [stats.latestFilingDate]);
  const sinceLabel = previousVisit
    ? stats.usedFallbackToFullSet
      ? `No newer filings than last visit (${previousVisit}); showing current export leaders`
      : `New since last visit after ${previousVisit}`
    : "First visit pulse from current export";
  return (
    <ActivityFeedStrip
      latestFilingDate={stats.latestFilingDate}
      sinceLabel={sinceLabel}
      newHighScoreBuys={stats.newHighScoreBuys}
      newClusters={stats.newClusters}
      watchlistHits={stats.watchlistHits}
      onOpenToday={onOpenToday}
      onOpenActionQueue={onOpenActionQueue}
      onOpenWorkspace={onOpenWorkspace}
    />
  );
}
